import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Row, Col } from 'antd'
import { TasksSection } from '../../../../tasks/components/index.ts'
import { InteractionsSection } from '../../../../interactions/components/index.ts'
import { DetailPanel, type DetailPanelItemType, type DetailPanelMode } from '../../../../deals/components/forms/parts/DetailPanel.tsx'
import { CompanyOverviewSection } from './CompanyOverviewSection.tsx'
import { RoundOverviewSection } from './RoundOverviewSection.tsx'
import { GeneralSection } from './GeneralSection.tsx'
import { InvestmentSummarySection } from './InvestmentSummarySection.tsx'
import { UserCardList } from '../../../../../core/components/UserCardList/index.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { AgentSignalsSection } from '../../../../internal_agents/components/AgentSignalsSection.tsx'
import { AgentDraftsSection } from '../../../../internal_agents/components/AgentDraftsSection.tsx'
import { CompanyBriefCard } from '../../../../internal_agents/components/CompanyBriefCard.tsx'
import { CompanyStatesStrip } from '../../../../internal_agents/components/CompanyStatesStrip.tsx'
import { CompanyKpisSection } from '../../../../internal_agents/components/CompanyKpisSection.tsx'
import { LpBriefCard } from '../../../../internal_agents/components/LpBriefCard.tsx'
import { LpFundraisingCard } from '../../../../internal_agents/components/LpFundraisingCard.tsx'
import { LpStatesStrip } from '../../../../internal_agents/components/LpStatesStrip.tsx'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { accountHasPortfolioRole, accountHasLpRole } from '../../../utils/accountRoles.ts'
import type { SourcingContactsByRole } from '../../../utils/fieldConfigs.ts'

import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../../state_manager/hooks/useAppDispatch'
import { createTask, updateTask, deleteTask as deleteTaskThunk } from '../../../../../store/slices/tasksSlice'
import {
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchInteractionsForAccount,
  delinkInteraction
} from '../../../../interactions/services/interactionsService.ts'
import {
  addInteraction,
  setInteractions,
  removeInteraction,
  updateInteraction as updateInteractionAction
} from '../../../../../state_manager/state_update_actions/interactions/interaction_actions.ts'
import { updateActiveRecord } from '../../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { fetchCompanyById } from '../../../services/companiesService.ts'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { CompanyTeamEntryFields } from '../../../../../state_manager/data_models/company_team_entry.ts'
import { UNKNOWN_CONTACT, SECTION_COMPANY_TEAM } from '../../../../../state_manager/constants/strings.ts'
import {
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY,
  RELATIONSHIP_TYPE_ADVISOR,
  RELATIONSHIP_TYPE_OTHER
} from '../../../../../state_manager/globals.ts'
import { AccountRecord } from '../../../../../state_manager/data_models/account_record.ts'
import { TaskRecordFields } from '../../../../../state_manager/data_models/task_record.ts'
import { InteractionRecordFields } from '../../../../../state_manager/data_models/interaction_record.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { setAccountAgentDrafts } from '../../../../relationships/services/relationshipsService.ts'

export function DashboardTab({
  currentCompany,
  formData,
  recordId,
  handleFieldChange,
  onPatchSuccess,
  handleTaskSelect,
  handleTaskAdd,
  handleInteractionSelect,
  handleInteractionAdd,
  handleDetailClose: handleDetailCloseParent,
  selectedItem,
  selectedItemType,
  companyTeamMembers: propCompanyTeamMembers,
  onCompanyTeamMembersChange,
  onRefreshInteractionsReady,
  onRefreshTasksReady,
  onRefreshInvestmentMeasuresReady,
  onRefreshAgentPanelsReady,
  onNavigateToAgentTab
}: {
  currentCompany?: AccountRecord | null
  formData?: Record<string, any>
  recordId?: string | null
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  handleTaskSelect?: (task: any) => void
  handleTaskAdd?: () => void
  handleInteractionSelect?: (interaction: any) => void
  handleInteractionAdd?: () => void
  handleDetailClose?: () => void
  selectedItem?: any
  selectedItemType?: DetailPanelItemType | null
  companyTeamMembers?: any[]
  onCompanyTeamMembersChange?: (members: any[]) => void
  onRefreshInteractionsReady?: (refreshFn: () => Promise<void>) => void
  onRefreshTasksReady?: (refreshFn: () => Promise<void>) => void
  onRefreshInvestmentMeasuresReady?: (refreshFn: () => Promise<void>) => void
  /** One aggregate refresher for the four internal-agent panels on this tab
   *  (states strip, crit-signals strip, Company Brief, KPI snapshot), so the
   *  company form's Refresh button reloads agent data alongside everything
   *  else. Aggregated here rather than plumbed panel-by-panel through Full. */
  onRefreshAgentPanelsReady?: (refreshFn: () => Promise<void>) => void
  /** Switches the company form to the Agent tab — the "View more" target when
   *  more open signals exist than the Current State card shows. */
  onNavigateToAgentTab?: () => void
}) {
  const dispatch = useAppDispatch()
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null)
  const [isTaskInEditMode, setIsTaskInEditMode] = useState(false)
  const relationshipCategories = useSelector((state: any) => state.globalConfigs?.relationship_categories || [])
  const refreshInvestmentMeasuresRef = useRef<(() => Promise<void>) | null>(null)
  const refreshCompanyStatesRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAgentSignalsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshCompanyBriefRef = useRef<(() => Promise<void>) | null>(null)
  const refreshCompanyKpisRef = useRef<(() => Promise<void>) | null>(null)
  // LP relationship manager panels (LP agent programme Phase 3).
  const refreshLpStatesRef = useRef<(() => Promise<void>) | null>(null)
  const refreshLpSignalsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshLpBriefRef = useRef<(() => Promise<void>) | null>(null)
  const refreshLpFundraisingRef = useRef<(() => Promise<void>) | null>(null)
  const refreshLpDraftsRef = useRef<(() => Promise<void>) | null>(null)

  const { detailsByCategory } = useLookups()
  const companyLookupId =
    detailsByCategory?.Globals?.find((option: any) => option.label === 'internal_company_id')?.value || null

  const [selectedContacts, setSelectedContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
  }>({
    introduced_by: [],
    sourced_by: []
  })

  const companyTeamMembers = propCompanyTeamMembers || []
  const setCompanyTeamMembers = onCompanyTeamMembersChange || (() => {})

  const data = currentCompany || formData || {}

  const mergedTeamMembersForDisplay = useMemo(() => {
    const getTypeIdFromCategoryId = (categoryId: string | null | undefined): string | null => {
      if (!categoryId) return null
      const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
      return category?.relationship_type_id || category?.relationshipTypeId || null
    }

    const contactGroups: Record<string, any> = {}

    companyTeamMembers.forEach((member: any) => {
      const contactId = member.contact?.value
      if (!contactId) return

      if (!member.founder && member.current !== true) {
        return
      }

      if (!member.founder) {
        const typeId = getTypeIdFromCategoryId(member.category)
        if (typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER) {
          return
        }
      }

      if (!contactGroups[contactId]) {
        contactGroups[contactId] = {
          key: contactId,
          contact: member.contact,
          founder: false,
          employeeRelationshipId: null,
          founderRelationshipId: null,
          categories: [],
          jobTitle: member.jobTitle || '',
          email: member.email || '',
          contactNumber: member.contactNumber || '',
          photo_url: member.photo_url || null
        }
      }

      if (member.founder) {
        contactGroups[contactId].founder = true
        contactGroups[contactId].founderRelationshipId = member.founderRelationshipId
      } else {
        contactGroups[contactId].employeeRelationshipId = member.employeeRelationshipId
        if (member.category && !contactGroups[contactId].categories.includes(member.category)) {
          contactGroups[contactId].categories.push(member.category)
        }
        if (member.jobTitle) {
          contactGroups[contactId].jobTitle = member.jobTitle
        }
      }

      if (member.photo_url) {
        contactGroups[contactId].photo_url = member.photo_url
      }
    })

    const allMembers = Object.values(contactGroups).map((group: any) => ({
      ...group,
      category: group.categories.length > 0 ? group.categories[0] : null,
      categories: group.categories
    }))

    return allMembers.sort((a: any, b: any) => {
      if (a.founder && !b.founder) return -1
      if (!a.founder && b.founder) return 1
      const nameA = a.contact?.label || a.email || ''
      const nameB = b.contact?.label || b.email || ''
      return nameA.localeCompare(nameB)
    })
  }, [companyTeamMembers, relationshipCategories])

  useEffect(() => {
    const otherTeam = data[AccountRecordFields.prog__other_team] || []

    const contactsByRole: SourcingContactsByRole = {
      introduced_by: [],
      sourced_by: []
    }

    if (otherTeam && Array.isArray(otherTeam) && otherTeam.length > 0) {
      const introducedByFromOtherTeam = otherTeam.filter(
        (contact: any) =>
          contact[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ===
          RELATIONSHIP_CATEGORY_INTRODUCED_BY
      )
      const sourcedByFromOtherTeam = otherTeam.filter(
        (contact: any) =>
          contact[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ===
          RELATIONSHIP_CATEGORY_SOURCED_BY
      )

      contactsByRole.introduced_by = introducedByFromOtherTeam.map((contact: any) => ({
        value:
          contact[UserEntryFields.contacts__id] || contact[CompanyTeamEntryFields.contacts__id] || contact.id || '',
        label:
          contact[UserEntryFields.prog__display_name] ||
          contact[CompanyTeamEntryFields.prog__display_name] ||
          `${contact[UserEntryFields.contacts__first_name] || contact[CompanyTeamEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || contact[CompanyTeamEntryFields.contacts__last_name] || ''}`.trim() ||
          contact[UserEntryFields.contacts__email] ||
          contact[CompanyTeamEntryFields.contacts__email] ||
          UNKNOWN_CONTACT,
        email: contact[UserEntryFields.contacts__email] || contact[CompanyTeamEntryFields.contacts__email] || '',
        data: contact
      }))

      contactsByRole.sourced_by = sourcedByFromOtherTeam.map((contact: any) => ({
        value:
          contact[UserEntryFields.contacts__id] || contact[CompanyTeamEntryFields.contacts__id] || contact.id || '',
        label:
          contact[UserEntryFields.prog__display_name] ||
          contact[CompanyTeamEntryFields.prog__display_name] ||
          `${contact[UserEntryFields.contacts__first_name] || contact[CompanyTeamEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || contact[CompanyTeamEntryFields.contacts__last_name] || ''}`.trim() ||
          contact[UserEntryFields.contacts__email] ||
          contact[CompanyTeamEntryFields.contacts__email] ||
          UNKNOWN_CONTACT,
        email: contact[UserEntryFields.contacts__email] || contact[CompanyTeamEntryFields.contacts__email] || '',
        data: contact
      }))
    }

    setSelectedContacts(contactsByRole)
  }, [data[AccountRecordFields.prog__other_team]])

  const [detailMode, setDetailMode] = useState<DetailPanelMode>('view')

  const handleTaskSelectInternal = useCallback(
    (task: any) => {
      if (task === null) {
        setDetailMode('view')
      } else {
        setDetailMode('view')
      }
      handleTaskSelect?.(task)
    },
    [handleTaskSelect]
  )

  const handleAddTaskInternal = useCallback(() => {
    setDetailMode('add-task')
    handleTaskAdd?.()
  }, [handleTaskAdd])

  const handleTaskEditModeChange = useCallback((isInEditMode: boolean) => {
    setIsTaskInEditMode(isInEditMode)
  }, [])

  const handleInteractionSelectInternal = useCallback(
    (interaction: any) => {
      setDetailMode('view')
      handleInteractionSelect?.(interaction)
    },
    [handleInteractionSelect]
  )

  const handleAddInteractionInternal = useCallback(() => {
    setDetailMode('add-interaction')
    handleInteractionAdd?.()
  }, [handleInteractionAdd])

  const handleDetailClose = useCallback(() => {
    setDetailMode('view')
    handleDetailCloseParent?.()
  }, [handleDetailCloseParent])

  const handleDetailEdit = useCallback((item: any, itemType: string) => {
    if (itemType === 'task') {
      setDetailMode('edit-task')
    } else if (itemType === 'interaction') {
      setDetailMode('edit-interaction')
    }
  }, [])

  const handleDetailDelete = useCallback(
    async (id: string) => {
      if (!recordId || !id) {
        throw new Error('Record ID is missing')
      }

      if (selectedItemType === 'task') {
        try {
          await dispatch(deleteTaskThunk({ dealId: recordId, taskId: id, recordType: 'company' })).unwrap()
          setTimeout(() => {
            handleDetailCloseParent?.()
          }, 100)
        } catch (error) {
          console.error('Error in handleDetailDelete:', error)
          setTimeout(() => {
            handleDetailCloseParent?.()
          }, 100)
          throw error
        }
        return
      }

      const interactionId = id
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction(recordId, interactionId, currentToken)
        dispatch(removeInteraction(recordId, interactionId))
        const refreshedInteractions = await fetchInteractionsForAccount(recordId, {}, currentToken)
        dispatch(setInteractions(recordId, refreshedInteractions))
        setTimeout(() => {
          handleDetailCloseParent?.()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelete:', error)
        setTimeout(() => {
          handleDetailCloseParent?.()
        }, 100)
        throw error
      }
    },
    [recordId, dispatch, handleDetailCloseParent, selectedItemType]
  )

  const handleDetailDelink = useCallback(
    async (interactionId: string) => {
      if (!recordId || !interactionId) {
        throw new Error('Record ID or Interaction ID is missing')
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        await delinkInteraction(recordId, interactionId, 'company', currentToken)
        dispatch(removeInteraction(recordId, interactionId))
        const refreshedInteractions = await fetchInteractionsForAccount(recordId, {}, currentToken)
        dispatch(setInteractions(recordId, refreshedInteractions))
        setTimeout(() => {
          handleDetailCloseParent?.()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelink:', error)
        setTimeout(() => {
          handleDetailCloseParent?.()
        }, 100)
        throw error
      }
    },
    [recordId, dispatch, handleDetailCloseParent]
  )

  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)

  const handleRefreshTasks = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  const handleDetailSubmit = useCallback(
    async (data: any, mode: string) => {
      if (!recordId || recordId === 'new') {
        console.warn('Cannot save task/interaction: Company ID is not available')
        return
      }

      try {
        if (mode === 'add-task') {
          const taskData = {
            ...data,
            task_accounts__account_id: recordId
          }
          const result = await dispatch(createTask({ dealId: recordId, taskData, recordType: 'company' }) as any)

          if (result.payload?.task && currentCompany) {
            const existingTasks = currentCompany[AccountRecordFields.prog__tasks] || []
            const updatedTasks = [result.payload.task, ...existingTasks]
            dispatch(
              updateActiveRecord({
                uuid: recordId,
                updates: {
                  [AccountRecordFields.prog__tasks]: updatedTasks
                }
              })
            )
          }
          await handleRefreshTasks()
        } else if (mode === 'edit-task') {
          const taskId = selectedItem?.[TaskRecordFields.tasks__id]
          const result = await dispatch(
            updateTask({
              dealId: recordId,
              taskId,
              taskData: data,
              recordType: 'company'
            }) as any
          )

          if (result.payload?.task && currentCompany) {
            const existingTasks = currentCompany[AccountRecordFields.prog__tasks] || []
            const taskIdFromResult = result.payload.task[TaskRecordFields.tasks__id] || taskId
            const updatedTasks = existingTasks.map((t) => {
              const existingTaskId = t?.[TaskRecordFields.tasks__id]
              if (existingTaskId?.toString() === taskIdFromResult?.toString()) {
                return result.payload.task
              }
              return t
            })
            dispatch(
              updateActiveRecord({
                uuid: recordId,
                updates: {
                  [AccountRecordFields.prog__tasks]: updatedTasks
                }
              })
            )
          }
          await handleRefreshTasks()
        } else if (mode === 'add-interaction') {
          const interactionData = {
            ...data,
            interaction_accounts__account_id: recordId
          }
          const currentToken = getAccessToken(store.getState().auth)
          const createdInteraction = await createInteraction(recordId, interactionData, currentToken, 'company')
          dispatch(addInteraction(recordId, createdInteraction as any))
        } else if (mode === 'edit-interaction') {
          const interactionId = selectedItem?.[InteractionRecordFields.interactions__id]
          const currentToken = getAccessToken(store.getState().auth)
          const updatedInteraction = await updateInteraction(recordId, interactionId, data, currentToken, 'company')
          dispatch(updateInteractionAction(recordId, interactionId, updatedInteraction as any))
        }

        setDetailMode('view')
        handleDetailCloseParent?.()
      } catch (error) {
        console.error('Failed to save:', error)
        throw error
      }
    },
    [recordId, selectedItem, handleDetailCloseParent, dispatch, currentCompany, handleRefreshTasks]
  )

  const isDetailPanelOpen = (selectedItem !== null && selectedItem !== undefined) || detailMode !== 'view'

  // Latest Valuation + Investment Summary are portfolio-only — hidden for
  // accounts without a Portfolio Company / Exited / Dead role (matches the
  // Holdings tab gate in Full.tsx).
  const showPortfolioSections = accountHasPortfolioRole(currentCompany)
  // LP-role accounts (active or target LPs) carry the relationship-manager
  // agent's panels: a crit-only signals strip (when the account is not also
  // a portfolio company — the company strip already shows every type's crit
  // signals) and the LP Brief. An account can be both.
  const showLpSections = accountHasLpRole(currentCompany)

  // The agent panels below each register their own loader; this rolls them
  // into the single refresher the company form's Refresh button drives. Guarded
  // on the role predicates so an account never calls a loader left behind by
  // a panel that is no longer rendered.
  const refreshAgentPanels = useCallback(async () => {
    if (!showPortfolioSections && !showLpSections) return
    await Promise.all(
      [
        ...(showPortfolioSections
          ? [
              refreshCompanyStatesRef.current,
              refreshAgentSignalsRef.current,
              refreshCompanyBriefRef.current,
              refreshCompanyKpisRef.current
            ]
          : []),
        ...(showLpSections
          ? [
              refreshLpStatesRef.current,
              refreshLpSignalsRef.current,
              refreshLpDraftsRef.current,
              refreshLpBriefRef.current,
              refreshLpFundraisingRef.current
            ]
          : [])
      ]
        .filter(Boolean)
        .map((refreshFn) => (refreshFn as () => Promise<void>)())
    )
  }, [showPortfolioSections, showLpSections])

  useEffect(() => {
    if (onRefreshAgentPanelsReady) onRefreshAgentPanelsReady(refreshAgentPanels)
  }, [onRefreshAgentPanelsReady, refreshAgentPanels])

  const selectedTaskId =
    isDetailPanelOpen && selectedItemType === 'task' ? selectedItem?.[TaskRecordFields.tasks__id] : null
  const selectedInteractionId =
    isDetailPanelOpen && selectedItemType === 'interaction'
      ? selectedItem?.[InteractionRecordFields.interactions__id]
      : null

  return (
    <div
      data-debug='dashboard-tab-container'
      data-component='DashboardTab'
      data-record-id={recordId}
      className='h-full flex flex-col'
      style={{ overflowY: 'auto', overflowX: 'hidden' }}
    >
      {/* Internal agents (owner-approved redesign 2026-08-18 + Phase 3 A3):
          the current-states strip carries the durable picture (health chip
          always, fundraising/exit/asks only while active); the signals strip
          below it carries the open exceptions — states are the no-touch layer,
          signals what needs eyes. Portfolio accounts only. */}
      {showPortfolioSections && recordId ? (
        <div data-debug='dashboard-tab-company-states-strip' className='mb-3'>
          <CompanyStatesStrip
            accountId={recordId}
            onRefreshReady={(refreshFn) => {
              refreshCompanyStatesRef.current = refreshFn
            }}
          />
        </div>
      ) : null}

      {/* The portfolio follower's open signals. Their own section since
          2026-09-06 (owner): they used to render chrome-less inside the strip
          above, which made them look like part of the Current State rather
          than the exceptions to it, and gave them no heading, no open count
          and nothing to collapse. Deliberately NOT filtered by agent type —
          on a dual-role account this is the one place every type's signals
          come together, which is why the LP strip below stays suppressed. */}
      {showPortfolioSections && recordId ? (
        <div data-debug='dashboard-tab-company-signals' className='mb-3'>
          <AgentSignalsSection
            contextId={recordId}
            openOnly
            maxVisible={3}
            hideWhenEmpty
            collapseKey='company_dashboard_signals'
            onViewMore={onNavigateToAgentTab}
            onRefreshReady={(refreshFn) => {
              refreshAgentSignalsRef.current = refreshFn
            }}
          />
        </div>
      ) : null}

      {/* LP account states (LP agent programme Phase 7) — the exact composition
          the portfolio strip above uses, which is the point: an LP Dashboard
          has the same two agent layers a portfolio one has, a durable Current
          State row over the open exceptions (owner decision 5). The signals
          half moved out of this strip's footer into its own section below the
          drafts (owner, 2026-09-06).

          Still gated on !showPortfolioSections: the company signals section
          above renders EVERY agent type's signals, so on a dual-role account
          the LP ones are already on the page. The LP STATES are a separate
          catalogue behind an agent_type_key filter and would not collide — a
          dual-role account could now be given this strip too, since the footer
          duplication that forced the exclusion is gone. Left as it was; it is
          a behaviour change, not a layout one. */}
      {showLpSections && !showPortfolioSections && recordId ? (
        <div data-debug='dashboard-tab-lp-states-strip' className='mb-3'>
          <LpStatesStrip
            accountId={recordId}
            lpStatusLabel={currentCompany?.comms_lp_statuses__label ?? null}
            lpStatusTagColor={currentCompany?.comms_lp_statuses__tag_color ?? null}
            draftsEnabled={currentCompany?.accounts__agent_drafts_enabled ?? null}
            draftsDefaultLabel={currentCompany?.prog__agent_drafts_default ?? null}
            onDraftsChange={async (next) => {
              // Per-LP agent drafts (PR D): the accounts PATCH route, then the
              // same slice update an InputManager autosave would make.
              try {
                await setAccountAgentDrafts(recordId, next, getAccessToken(store.getState().auth))
                onPatchSuccess?.(recordId, { accounts__agent_drafts_enabled: next })
              } catch (error) {
                showErrorToast(error instanceof Error ? error.message : 'Failed to update agent drafts')
              }
            }}
            onRefreshReady={(refreshFn) => {
              refreshLpStatesRef.current = refreshFn
            }}
            onTaskCreated={handleRefreshTasks}
          />
        </div>
      ) : null}

      {/* Drafts before signals (owner, 2026-09-06). The 2026-09-04 order was
          signal-then-remedy — "touchpoint due" followed by the email that
          discharges it — but with the signals inside the strip's footer that
          put every open exception ahead of the one thing needing a click, and
          a live LP carries more of them than fits: University of Michigan has
          six states and seven open signals above its two proposed emails.
          A draft is the action item, so it leads. Capped at 2 and hidden when
          empty — a draft card renders the whole email — with View more through
          to the full list.

          Gated on showLpSections ALONE, unlike the LP strip above it. Drafts
          have no twin — nothing else on this Dashboard renders them — so a
          portfolio company that is also an LP gets its drafts here rather than
          only on the LP Agent tab. */}
      {showLpSections ? (
        <div data-debug='dashboard-tab-lp-drafts' className='mb-3'>
          <AgentDraftsSection
            contextTable='accounts'
            contextId={recordId}
            hideWhenEmpty
            maxVisible={2}
            onViewMore={onNavigateToAgentTab}
            onRefreshReady={(refreshFn) => {
              refreshLpDraftsRef.current = refreshFn
            }}
            collapseKey='lp_dashboard_drafts'
          />
        </div>
      ) : null}

      {/* The relationship manager's open signals — the attention layer, under
          the action layer. Filtered to lp_relationship_manager, and gated the
          same way the LP strip is: the company signals section above already
          shows these unfiltered on a dual-role account.

          Deliberately NOT crit-only (owner, 2026-09-03). It was, and that hid
          the relationship manager almost entirely: only relationship_risk
          defaults to crit out of its nine kinds, and the model downgrades even
          that one when the evidence is thin — a target LP with a touchpoint
          due, an unanswered inbound and a re-up opportunity showed nothing. */}
      {showLpSections && !showPortfolioSections && recordId ? (
        <div data-debug='dashboard-tab-lp-signals-section' className='mb-3'>
          <AgentSignalsSection
            contextId={recordId}
            openOnly
            maxVisible={3}
            hideWhenEmpty
            collapseKey='lp_dashboard_signals'
            agentTypeKeys={['lp_relationship_manager']}
            onViewMore={onNavigateToAgentTab}
            onRefreshReady={(refreshFn) => {
              refreshLpSignalsRef.current = refreshFn
            }}
          />
        </div>
      ) : null}

      {/* flex: 1 0 auto — grows into the tab's remaining height (a sparse
          left column without a Company Brief no longer collapses the
          Tasks/Interactions column) and never shrinks below its content.
          The .dashboard-tab-row stylesheet rules are not loaded. */}
      <Row
        data-debug='dashboard-tab-main-row'
        gutter={[16, 0]}
        className='dashboard-tab-row dashboard-tab-row--content-height grow shrink-0'
      >
        <Col span={isDetailPanelOpen ? 7 : 16}>
          <div data-debug='dashboard-tab-left-column'>
            <Row data-debug='dashboard-tab-left-subrow' gutter={[12, 12]}>
              {/* Fundraising position leads the column for LP-role accounts
                  (owner, 2026-09-03): where each open process stands and
                  whose move it is, ahead of even Company Overview. It is the
                  single most time-sensitive thing on an investor's page —
                  the LP Brief is standing narrative and can sit further down
                  with Sourcing/Company Team; this is today's action item. */}
              {showLpSections && recordId ? (
                <Col span={24}>
                  <div data-debug='dashboard-tab-lp-fundraising-section'>
                    <LpFundraisingCard
                      accountId={recordId}
                      hideWhenEmpty
                      onRefreshReady={(refreshFn) => {
                        refreshLpFundraisingRef.current = refreshFn
                      }}
                    />
                  </div>
                </Col>
              ) : null}

              {/* Owner layout (2026-08-24): the overview takes one column with
                  the money panels stacked beside it. Non-portfolio accounts
                  have no Investment Summary or Latest Valuation, so the
                  overview keeps the full width there rather than sitting next
                  to an empty half. */}
              <Col span={isDetailPanelOpen ? 24 : showPortfolioSections ? 12 : 24}>
                <div data-debug='dashboard-tab-overview-section'>
                  <CompanyOverviewSection
                    company={currentCompany}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    onPatchSuccess={onPatchSuccess}
                    showTitle={true}
                    recordId={recordId}
                  />
                </div>
              </Col>

              {/* Investment Summary with Latest Valuation beneath it, stacked
                  INSIDE one Col: three sibling half-width Cols would wrap the
                  third back to the left, putting the valuation under the
                  overview instead of under the summary. */}
              {showPortfolioSections && recordId ? (
                <Col span={isDetailPanelOpen ? 24 : 12}>
                  <div className='space-y-3'>
                    <div data-debug='dashboard-tab-investment-summary-section'>
                      <InvestmentSummarySection
                        accountId={recordId}
                        isDrawer={false}
                        onRefreshRequest={(refreshFn) => {
                          refreshInvestmentMeasuresRef.current = refreshFn
                          if (onRefreshInvestmentMeasuresReady) {
                            onRefreshInvestmentMeasuresReady(refreshFn)
                          }
                        }}
                      />
                    </div>

                    <div data-debug='dashboard-tab-round-overview-section'>
                      <RoundOverviewSection
                        company={currentCompany}
                        formData={formData}
                        handleFieldChange={handleFieldChange}
                        onPatchSuccess={onPatchSuccess}
                        recordId={recordId}
                        isReadOnly={true}
                      />
                    </div>
                  </div>
                </Col>
              ) : null}

              {/* The LP Brief (LP agent programme Phase 3) sits above
                  Sourcing / KPIs / Company Team (owner, 2026-09-03): standing
                  relationship narrative, read less urgently than the
                  Fundraising panel at the top of the column but still ahead
                  of the free-text Sourcing note and the contact roster. */}
              {showLpSections && recordId ? (
                <Col span={24}>
                  <div data-debug='dashboard-tab-lp-brief-section'>
                    <LpBriefCard
                      accountId={recordId}
                      onRefreshReady={(refreshFn) => {
                        refreshLpBriefRef.current = refreshFn
                      }}
                    />
                  </div>
                </Col>
              ) : null}

              {/* The free-text General section replaces the portfolio money
                  panels for non-portfolio accounts (still on Details too). */}
              {!showPortfolioSections ? (
                <Col span={24}>
                  <div data-debug='dashboard-tab-general-section'>
                    <GeneralSection
                      company={currentCompany}
                      formData={formData}
                      handleFieldChange={handleFieldChange}
                      onPatchSuccess={onPatchSuccess}
                      recordId={recordId}
                    />
                  </div>
                </Col>
              ) : null}

              {/* KPI snapshot (A1) spans both columns beneath the overview and
                  the valuation (owner layout 2026-08-24, superseding the
                  earlier "option B / under the brief" placement). Hides itself
                  until the first extracted value lands. */}
              {showPortfolioSections && recordId ? (
                <Col span={24}>
                  <div data-debug='dashboard-tab-company-kpis-section'>
                    <CompanyKpisSection
                      accountId={recordId}
                      onRefreshReady={(refreshFn) => {
                        refreshCompanyKpisRef.current = refreshFn
                      }}
                    />
                  </div>
                </Col>
              ) : null}

              <Col span={24}>
                <div data-debug='dashboard-tab-company-team-section'>
                  <FormSection
                    data-debug='company-team-section'
                    data-component='CompanyTeamSection'
                    title={SECTION_COMPANY_TEAM}
                    titleColor='blue'
                    className='shadow-sm border border-gray-200'
                  >
                    <UserCardList members={mergedTeamMembersForDisplay} title='' />
                  </FormSection>
                </div>
              </Col>

              {/* The Company Brief closes the column (owner layout 2026-08-24).
                  It remains the Dashboard narrative for portfolio accounts. */}
              {showPortfolioSections && recordId ? (
                <Col span={24}>
                  <div data-debug='dashboard-tab-company-brief-section'>
                    <CompanyBriefCard
                      accountId={recordId}
                      onRefreshReady={(refreshFn) => {
                        refreshCompanyBriefRef.current = refreshFn
                      }}
                    />
                  </div>
                </Col>
              ) : null}

            </Row>
          </div>
        </Col>

        <Col span={isDetailPanelOpen ? 7 : 8}>
          {/* The rail is taken out of flow so it never drives the row height:
              it stretches to the left column instead, and Interactions scrolls
              inside it rather than clipping at the fold. */}
          <div className='relative h-full min-h-[400px]'>
            <div
              data-debug='dashboard-tab-right-column'
              className='absolute inset-0 flex flex-col overflow-hidden'
            >
              <div data-debug='dashboard-tab-tasks-section' className='flex-shrink-0 mb-2'>
              <TasksSection
                recordId={recordId}
                recordType='company'
                onTaskSelect={handleTaskSelectInternal}
                selectedTaskId={selectedTaskId}
                onAddTask={handleAddTaskInternal}
                onEditModeChange={handleTaskEditModeChange}
                onRefreshRequest={(refreshFn: () => Promise<void>) => {
                  refreshTasksRef.current = refreshFn
                  if (onRefreshTasksReady) {
                    onRefreshTasksReady(refreshFn)
                  }
                }}
              />
            </div>

              <div data-debug='dashboard-tab-interactions-section' className='flex-1 min-h-0'>
              <InteractionsSection
                recordId={recordId}
                recordType='company'
                onInteractionSelect={handleInteractionSelectInternal}
                selectedInteractionId={selectedInteractionId}
                onAddInteraction={handleAddInteractionInternal}
                onRefreshRequest={(refreshFn: () => Promise<void>) => {
                  refreshInteractionsRef.current = refreshFn
                  if (onRefreshInteractionsReady) {
                    onRefreshInteractionsReady(refreshFn)
                  }
                }}
              />
              </div>
            </div>
          </div>
        </Col>

        {isDetailPanelOpen && (
          <Col span={10} className='h-full'>
            <div data-debug='dashboard-tab-detail-column' className='h-full flex flex-col overflow-hidden'>
              <DetailPanel
                selectedItem={selectedItem}
                itemType={selectedItemType}
                mode={detailMode}
                onClose={handleDetailClose}
                onEdit={handleDetailEdit}
                onSubmit={handleDetailSubmit}
                onDelete={handleDetailDelete}
                onDelink={handleDetailDelink}
                onRefreshInteractions={async () => {
                  if (refreshInteractionsRef.current) {
                    await refreshInteractionsRef.current()
                  }
                }}
                onRefreshTasks={handleRefreshTasks}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  )
}
